import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Play, Users, RefreshCw, Trash2, CheckCircle, AlertCircle,
  ArrowLeft, Database, Zap, Shield
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContextMySQL';

// Demo data generators
const DEMO_FIRST_NAMES = [
  'Ahmed', 'Youssef', 'Mehdi', 'Karim', 'Sami', 'Hassan', 'Omar', 'Amine', 
  'Badr', 'Reda', 'Soufiane', 'Anas', 'Walid', 'Nabil', 'Adil', 'Jamal', 
  'Rachid', 'Moussa', 'Ilyas', 'Yassin', 'Hamza', 'Othmane', 'Ayoub', 'Zakaria',
  'Imad', 'Marouane', 'Aymane', 'Mohamed', 'Ali', 'Said', 'Brahim', 'Khalid'
];

const DEMO_LAST_NAMES = [
  'Alami', 'Benali', 'Chakir', 'Dahbi', 'El Fassi', 'Filali', 'Ghazi', 'Hassani',
  'Idrissi', 'Jaouhari', 'Kadiri', 'Lahmidi', 'Moussaoui', 'Naciri', 'Oubaha', 'Rami',
  'Sahraoui', 'Tahiri', 'Wahabi', 'Zahraoui', 'Bennani', 'Fahim', 'Harrouchi', 'Kabbaj'
];

const DOMAINS = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'dofus.ma', 'kamas.trade'];

const generateFakeEmail = (first, last, index = 0) => {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 10000);
  const patterns = [
    `${first}.${last}.${random}`,
    `${first}_${last}_${timestamp}`,
    `${first}${last}${random}`,
    `${first[0]}${last}${random}`,
    `${first}.${last}.${index}.${random}`,
    `${first}_${last}_${index}_${timestamp.toString().slice(-4)}`
  ];
  const pattern = patterns[Math.floor(Math.random() * patterns.length)];
  const domain = DOMAINS[Math.floor(Math.random() * DOMAINS.length)];
  return `${pattern.toLowerCase()}@${domain}`;
};

const generateFakePhone = () => {
  const prefixes = ['06', '07', '05'];
  const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
  return `${prefix}${Math.floor(Math.random() * 100000000).toString().padStart(8, '0')}`;
};

// Servers and games data for P2P offers
const P2P_SERVERS = {
  'DOFUS 3': ['Draconiros', 'Hellmina', 'Brumen', 'Talpa', 'Thanatena', 'Oshimo', 'Imagiro'],
  'DOFUS RETRO': ['Brial', 'Crail', 'Erzal', 'Galgarion'],
  'DOFUS TOUCH': ['Dodge', 'Grandapan', 'Herdegrize']
};

const PAYMENT_METHODS = ['Bank Transfer', 'Cash'];
const TERMS_LIST = [
  'Fast delivery within 30 minutes - Verified trader',
  'Contact me before placing order - Available 9AM-11PM',
  'Available 24/7 for trades - Premium service',
  'Best rates guaranteed - Price matching available',
  'Trusted trader with 500+ successful trades',
  'Professional service - Refund if delayed',
  'Secure trading - Escrow protection included',
  'Multiple payment methods accepted',
  'Fast response time - Under 5 minutes',
  'VIP support for large orders (>50M Kamas)',
  'New trader discount - First order 10% off',
  'Bulk trading discounts available'
];

const createOffersForUser = (user) => {
  const offers = [];
  const timestamp = Date.now();
  
  // Select random server from each game
  Object.entries(P2P_SERVERS).forEach(([game, servers], gameIndex) => {
    const server = servers[Math.floor(Math.random() * servers.length)];
    
    // Base market price simulation
    const basePrice = game === 'DOFUS 3' ? 12 : game === 'DOFUS RETRO' ? 7 : 15;
    const priceVariation = (Math.random() - 0.5) * 2; // ±1 DH variation
    const marketPrice = Number((basePrice + priceVariation).toFixed(2));
    
    // Create BUY offer (user wants to buy Kamas)
    const buyPrice = Number((marketPrice * 0.92).toFixed(2)); // 8% below market
    offers.push({
      id: `offer_buy_${user.id}_${gameIndex}`,
      userId: user.id,
      traderName: user.name,
      game,
      server,
      type: 'buy',
      price: buyPrice,
      minAmount: Math.floor(Math.random() * 5 + 1) * 1000000, // 1M to 5M
      maxAmount: Math.floor(Math.random() * 50 + 10) * 1000000, // 10M to 60M
      stock: Math.floor(Math.random() * 100 + 50) * 1000000, // 50M to 150M
      paymentMethods: PAYMENT_METHODS.slice(0, Math.floor(Math.random() * 3) + 2),
      terms: TERMS_LIST.slice(Math.floor(Math.random() * 3), Math.floor(Math.random() * 3) + 2).join(' | '),
      status: 'active',
      createdAt: new Date(timestamp - Math.random() * 86400000 * 7).toISOString(),
      rating: Number((Math.random() * 2 + 3).toFixed(1)), // 3.0 to 5.0
      trades: Math.floor(Math.random() * 100)
    });
    
    // Create SELL offer (user wants to sell Kamas)
    const sellPrice = Number((marketPrice * 1.08).toFixed(2)); // 8% above market
    offers.push({
      id: `offer_sell_${user.id}_${gameIndex}`,
      userId: user.id,
      traderName: user.name,
      game,
      server,
      type: 'sell',
      price: sellPrice,
      minAmount: Math.floor(Math.random() * 5 + 1) * 1000000,
      maxAmount: Math.floor(Math.random() * 50 + 10) * 1000000,
      stock: Math.floor(Math.random() * 200 + 100) * 1000000, // 100M to 300M
      paymentMethods: PAYMENT_METHODS.slice(0, Math.floor(Math.random() * 3) + 2),
      terms: TERMS_LIST.slice(Math.floor(Math.random() * 3), Math.floor(Math.random() * 3) + 2).join(' | '),
      status: 'active',
      createdAt: new Date(timestamp - Math.random() * 86400000 * 7).toISOString(),
      rating: Number((Math.random() * 2 + 3).toFixed(1)),
      trades: Math.floor(Math.random() * 100)
    });
  });
  
  return offers;
};

const API_URL = 'http://localhost/kamas-vente/backend/api';

const addOffersToP2P = async (newOffers) => {
  try {
    console.log('Sending offers to API:', newOffers.length, 'offers');
    const response = await fetch(`${API_URL}/offers.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bulk: true, offers: newOffers })
    });
    const text = await response.text();
    console.log('Raw API response:', text);
    try {
      // Extract JSON from mixed HTML+JSON response
      const jsonMatch = text.match(/\{.*\}/);
      const jsonStr = jsonMatch ? jsonMatch[0] : text;
      const data = JSON.parse(jsonStr);
      console.log('Parsed API response:', data);
      return data.created || 0;
    } catch (parseErr) {
      console.error('JSON parse error. Raw response:', text);
      return 0;
    }
  } catch (err) {
    console.error('Error adding offers:', err);
    return 0;
  }
};

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <motion.div 
      className="bg-gray-900/80 border border-gray-800 rounded-2xl p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center mb-4`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <p className="text-gray-400 text-sm">{label}</p>
      <p className="text-3xl font-bold">{value}</p>
    </motion.div>
  );
}

export default function DemoPage() {
  const { user, users, register, generateBulkUsers, clearAllDemoData } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedCount, setGeneratedCount] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [demoStats, setDemoStats] = useState({
    totalUsers: 0,
    totalOrders: 0
  });

  useEffect(() => {
    // Load saved demo stats
    const saved = localStorage.getItem('demoStats');
    if (saved) {
      setDemoStats(JSON.parse(saved));
    } else {
      updateDemoStats();
    }
  }, []);

  useEffect(() => {
    updateDemoStats();
  }, [users]);

  const updateDemoStats = () => {
    const stats = {
      totalUsers: users?.length || 0,
      totalOrders: JSON.parse(localStorage.getItem('chatHistory') || '[]').length
    };
    setDemoStats(stats);
    localStorage.setItem('demoStats', JSON.stringify(stats));
  };

  const generateDemoUsers = async (count) => {
    setIsGenerating(true);
    setGeneratedCount(0);

    const newUsers = [];
    
    for (let i = 0; i < count; i++) {
      const firstName = DEMO_FIRST_NAMES[Math.floor(Math.random() * DEMO_FIRST_NAMES.length)];
      const lastName = DEMO_LAST_NAMES[Math.floor(Math.random() * DEMO_LAST_NAMES.length)];
      
      const timestamp = Date.now();
      const randomNum = Math.floor(Math.random() * 10000);
      const userData = {
        id: `demo_${timestamp}_${randomNum}_${i}`,
        name: `${firstName} ${lastName}`,
        email: generateFakeEmail(firstName, lastName, i),
        phone: generateFakePhone(),
        password: 'demo123456',
        isDemo: true,
        createdAt: new Date(Date.now() - Math.random() * 86400000 * 30).toISOString()
      };
      
      newUsers.push(userData);
      setGeneratedCount(i + 1);
      
      // Small delay for visual effect
      if (i % 10 === 0) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    // Register all users using bulk method
    let createdUsers = [];
    try {
      createdUsers = await generateBulkUsers(newUsers);
      console.log(`Generated ${createdUsers.length} demo users`);
    } catch (err) {
      console.error('Error generating bulk users:', err);
    }

    setIsGenerating(false);
    setShowSuccess(true);
    updateDemoStats();
    
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const handleResetDemo = () => {
    if (window.confirm('⚠️ Cette action va supprimer TOUTES les données de démo (utilisateurs, commandes). Êtes-vous sûr?')) {
      clearAllDemoData?.();
      localStorage.removeItem('demoStats');
      localStorage.removeItem('chatHistory');
      localStorage.removeItem('completedOrder');
      
      // Reload to reset all contexts
      window.location.reload();
    }
  };

  const quickLoginAsDemo = () => {
    if (users && users.length > 0) {
      const randomUser = users[Math.floor(Math.random() * users.length)];
      // Login logic would go here
      alert(`Connexion en tant que: ${randomUser.name}\nEmail: ${randomUser.email}\nPassword: demo123456`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <nav className="border-b border-gray-800 bg-gray-950/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <ArrowLeft size={20} className="text-gray-400" />
              <span className="text-gray-400 hover:text-white">Retour</span>
            </Link>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Zap className="text-yellow-400" size={24} />
              Mode Démo Client
            </h1>
            <div className="w-24" />
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero */}
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h2 className="text-4xl font-bold mb-4">
            🎮 Démo Prête pour Client
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Générez instantanément des données réalistes pour impressionner vos clients. 
            Vendeurs, acheteurs, transactions et chat P2P complets.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-2 gap-4 mb-12">
          <StatCard 
            icon={Users} 
            label="Utilisateurs"
            value={demoStats.totalUsers} 
            color="bg-blue-500"
          />
          <StatCard 
            icon={Shield} 
            label="Commandes"
            value={demoStats.totalOrders} 
            color="bg-yellow-500"
          />
        </div>

        {/* Success Message */}
        {showSuccess && (
          <motion.div 
            className="bg-green-500/20 border border-green-500/50 text-green-400 px-6 py-4 rounded-xl mb-8 flex items-center gap-3"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <CheckCircle size={24} />
            <span className="font-medium">Génération terminée! {generatedCount} utilisateurs créés avec succès.</span>
          </motion.div>
        )}

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Generate Users */}
          <motion.div 
            className="bg-gray-900/80 border border-gray-800 rounded-2xl p-6"
            whileHover={{ borderColor: 'rgba(234, 179, 8, 0.3)' }}
          >
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Users className="text-yellow-400" />
              Générer Utilisateurs Fake
            </h3>
            <p className="text-gray-400 mb-6">
              Créez instantanément des comptes avec emails, téléphones et noms réalistes marocains.
              Tous les mots de passe sont: <code className="bg-gray-800 px-2 py-1 rounded">demo123456</code>
            </p>
            
            <div className="grid grid-cols-3 gap-3">
              {[10, 25, 50].map((count) => (
                <motion.button
                  key={count}
                  onClick={() => generateDemoUsers(count)}
                  disabled={isGenerating}
                  className="py-3 px-4 bg-gray-800 hover:bg-yellow-500/20 border border-gray-700 hover:border-yellow-500/50 rounded-xl font-medium transition-colors disabled:opacity-50"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isGenerating ? (
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto" />
                  ) : (
                    `+${count}`
                  )}
                </motion.button>
              ))}
            </div>
            
            {isGenerating && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-400">Génération en cours...</span>
                  <span className="text-yellow-400">{generatedCount} créés</span>
                </div>
                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-yellow-500"
                    initial={{ width: 0 }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 2 }}
                  />
                </div>
              </div>
            )}
          </motion.div>

          {/* Demo Tools */}
          <motion.div 
            className="bg-gray-900/80 border border-gray-800 rounded-2xl p-6"
            whileHover={{ borderColor: 'rgba(234, 179, 8, 0.3)' }}
          >
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Database className="text-blue-400" />
              Outils de Démo
            </h3>
            
            <div className="space-y-3">
              <button
                onClick={quickLoginAsDemo}
                disabled={!users || users.length === 0}
                className="w-full py-3 px-4 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/50 rounded-xl font-medium flex items-center gap-3 disabled:opacity-50"
              >
                <Play size={18} />
                Connexion Rapide (Random User)
              </button>
              
              <Link
                to="/buy"
                className="w-full py-3 px-4 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-xl font-medium flex items-center gap-3 block text-center"
              >
                <Database size={18} />
                Tester Achat Kamas
              </Link>
              
              <button
                onClick={handleResetDemo}
                className="w-full py-3 px-4 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 rounded-xl font-medium flex items-center gap-3 text-red-400"
              >
                <Trash2 size={18} />
                Réinitialiser TOUTE la Démo
              </button>
            </div>
          </motion.div>
        </div>

        {/* Info Section */}
        <div className="bg-gradient-to-r from-yellow-500/10 to-transparent border border-yellow-500/30 rounded-2xl p-6 mb-8">
          <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
            <AlertCircle className="text-yellow-400" size={20} />
            Comment utiliser cette démo
          </h3>
          <ul className="space-y-2 text-gray-400 text-sm">
            <li>• <strong>Générez des utilisateurs</strong> - Crée des comptes avec noms marocains réalistes</li>
            <li>• <strong>Testez l'achat</strong> - Cliquez sur "Acheter" pour créer une commande et chatter avec le support</li>
            <li>• <strong>Testez la vente</strong> - Cliquez sur "Vendre" pour vendre vos Kamas</li>
            <li>• <strong>Historique</strong> - Voir toutes vos commandes passées via "Mes Commandes"</li>
            <li>• <strong>Reset complet</strong> - Efface tout pour recommencer la démo</li>
          </ul>
        </div>

        {/* Sample Users Preview */}
        {Array.isArray(users) && users.length > 0 && (
          <motion.div
            className="bg-gray-900/80 border border-gray-800 rounded-2xl p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <h3 className="text-lg font-bold mb-4">Utilisateurs Générés ({users.length})</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-64 overflow-y-auto">
              {users.slice(-12).reverse().map((u, idx) => (
                <div key={u.id || idx} className="bg-gray-800/50 rounded-lg p-3 text-sm">
                  <p className="font-medium text-white">{u?.name || 'Unknown'}</p>
                  <p className="text-gray-400 text-xs">{u?.email || 'no-email'}</p>
                  <p className="text-gray-500 text-xs mt-1">📱 {u?.phone || 'N/A'}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
